import React from 'react';
import classNames from 'classnames';
import Tooltip from 'rc-tooltip';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons/faComments';

import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';
import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import AnonymousUserDisplay from 'buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import EmployerAssociation from 'buyside/components/presentation/EmployerAssociation/employerAssociation';

import { defaultUserImageUrl, userUrl } from 'buyside/util/urlHelper';

import { IdeaShowPageCommentFragment, Maybe } from 'interfaces/graphql-typings';

import styles from './commentHeaderStyles.scss';

const CommentHeader: React.FC<{
  anonUser: IdeaShowPageCommentFragment['anonUser'];
  authorName: Maybe<string>;
  createdAt: string;
  isAdmin: boolean;
  isAuthor: boolean;
  isAuthorComment: boolean;
  isNonBuyside: boolean;
  responseRequested: boolean;
  showUserInfo: boolean;
  user: IdeaShowPageCommentFragment['user'];
}> = ({
  anonUser,
  authorName,
  createdAt,
  isAdmin,
  isAuthor,
  isAuthorComment,
  isNonBuyside,
  responseRequested,
  showUserInfo,
  user
}) => {
  let commenter;
  let commenterEmployer;

  if (user) {
    const { id, anonymousUserId, displayName, mostRecentEmployer } = user;
    commenter = showUserInfo ? (
      <a href={id === '26726' ? undefined : userUrl(id)}>
        {isNonBuyside && isAuthor ? authorName : displayName}
      </a>
    ) : (
      <>
        <AnonymousUserDisplay anonUser={{ title: mostRecentEmployer?.title }} /> ({anonymousUserId})
      </>
    );
    commenterEmployer = showUserInfo ? (
      <EmployerAssociation mostRecentEmployer={mostRecentEmployer} style={styles.employerDisplay} />
    ) : (
      <AnonEmployerAssociation
        aumRange={mostRecentEmployer?.employer?.aumRange}
        fundType={mostRecentEmployer?.employer?.fundType}
        employerStyles={styles.employerDisplay}
      />
    );
  } else if (anonUser) {
    const { anonymousUserId, aumRange, fundType } = anonUser;
    commenter = (
      <>
        <AnonymousUserDisplay anonUser={anonUser} /> ({anonymousUserId})
      </>
    );
    commenterEmployer = (
      <AnonEmployerAssociation
        aumRange={aumRange}
        fundType={fundType}
        employerStyles={styles.employerDisplay}
      />
    );
  }

  return (
    <div className={styles.commentHeader}>
      <div className={styles.profileImage}>
        <ProfileImage
          source={user && showUserInfo ? user.profileImageUrl : defaultUserImageUrl()}
          size={IMAGE_SIZES.SMALL}
        />
      </div>
      <div className={classNames(styles.userDisplay)}>
        <div className={styles.userName}>{commenter}</div>
        <div className={styles.employerRow}>{commenterEmployer}</div>
        <div className={styles.dateRow}>
          {moment(new Date(createdAt || '')).format('MMM DD, YYYY')}
        </div>
      </div>
      {responseRequested && (isAuthor || isAdmin) && (
        <Tooltip
          overlay="The commentor has requested your response to this comment as the idea author"
          placement="top"
        >
          <span className={styles.responseRequest}>
            <FontAwesomeIcon icon={faComments} />
            YOUR RESPONSE REQUESTED
          </span>
        </Tooltip>
      )}
      {isAuthorComment ? <span className={styles.responseRequest}>AUTHOR</span> : null}
    </div>
  );
};

export default CommentHeader;
